/**
 * Fatima Al-Zaabi (HRT Pathway HRT Optimization) - PROD-579 fixture.
 *
 * Source-of-truth: HTML-PROTOTYPE-PARITY-AUDIT §A.11 (51F, perimenopausal,
 * FSH 42, PHQ-9 9, BRCA flag exclusion handled in HRT Pathway SOAP) +
 * ORIGINAL-PROTOTYPE-PROMPTS Prompt 8 (HRT Pathway = HRT Optimization). Verbatim
 * NBA copy from HTML-BEHAVIORAL-EXTRACTION §3 HRT Pathway (em dashes rewritten
 * per HC5).
 *
 * Tenant: longevity-health-institute. Future PROD-568 wires tenant
 * filtering so this profile is visible only when active tenant is
 * Longevity.
 *
 * Per HC1 + ADR-PROTO-002 mitigation 4: every value is synthetic.
 * Per HC5: zero em dashes.
 */
import type { PatientFixture } from './types';

export const fatimaAlZaabi: PatientFixture = {
  id: 'fatima-al-zaabi',
  name: 'Fatima Al-Zaabi',
  age: 51,
  sex: 'F',
  tenant: 'longevity-health-institute',
  pathway: 'hrt-pathway',
  presetClinicalQuestion:
    'Perimenopausal symptom burden. Confirm HRT Pathway HRT eligibility and dispatch MenQoL plus PSQI.',
  summary:
    'Perimenopausal, FSH 42, PHQ-9 9, sleep fragmentation. HRT Pathway Step 1 HRT review.',
  chart: {
    problemList: [
      'Perimenopause with vasomotor symptoms',
      'Insomnia, fragmented sleep',
      'Mild depressive symptoms (PHQ-9 = 9)',
      'Mixed hyperlipidemia',
      'Vitamin D insufficiency',
    ],
    medications: [
      'Cholecalciferol 2000 IU PO daily',
      'Magnesium glycinate 300 mg PO at bedtime',
      'Multivitamin PO daily',
    ],
    vitals: {
      bmi: 26.4,
      heightCm: 162,
      weightKg: 69.3,
      bloodPressure: '124/78 mmHg',
      heartRate: 72,
      temperatureC: 36.8,
    },
    lastVisit: {
      date: '2026-03-30',
      summary:
        'Perimenopausal symptoms increasing over 6 months. Discussed HRT options. Ordered baseline hormone panel and PSQI plus MenQoL screening.',
    },
  },
  labs: {
    HbA1c: {
      value: 5.4,
      unit: '%',
      referenceRange: '4.0 to 5.6',
      collectedAt: '2026-03-30',
    },
    LDL: {
      value: 124,
      unit: 'mg/dL',
      referenceRange: 'less than 100',
      collectedAt: '2026-03-30',
    },
    ApoB: {
      value: 96,
      unit: 'mg/dL',
      referenceRange: 'less than 90',
      collectedAt: '2026-03-30',
    },
    eGFR: {
      value: 88,
      unit: 'mL/min/1.73m2',
      referenceRange: 'greater than 60',
      collectedAt: '2026-03-30',
    },
    hsCRP: {
      value: 1.6,
      unit: 'mg/L',
      referenceRange: 'less than 1.0',
      collectedAt: '2026-03-30',
    },
    HDL: {
      value: 58,
      unit: 'mg/dL',
      referenceRange: 'greater than 50',
      collectedAt: '2026-03-30',
    },
    triglycerides: {
      value: 138,
      unit: 'mg/dL',
      referenceRange: 'less than 150',
      collectedAt: '2026-03-30',
    },
    estradiol: {
      value: 28,
      unit: 'pg/mL',
      referenceRange: 'follicular 30 to 120',
      collectedAt: '2026-03-30',
    },
    FSH: {
      value: 42,
      unit: 'mIU/mL',
      referenceRange: 'premenopausal less than 30',
      collectedAt: '2026-03-30',
    },
    TSH: {
      value: 2.4,
      unit: 'mIU/L',
      referenceRange: '0.4 to 4.5',
      collectedAt: '2026-03-30',
    },
  },
  // ICD-10: perimenopausal symptoms, insomnia, mild depressive episode,
  // mixed hyperlipidemia, vitamin D deficiency.
  icd10: ['N95.1', 'G47.00', 'F32.0', 'E78.5', 'E55.9'],
  nbas: [
    {
      id: 'fatima-tlp2-nba-1',
      title: 'Order hormone baseline panel',
      rationale:
        'FSH, LH, Estradiol, Total Testosterone, SHBG, DHEA-S required for HRT Pathway Step 2 gate.',
      confidence: 0.93,
      riskTier: 'manual_approve',
      actionType: 'order_lab',
      ctaVerb: 'Order',
      cited_sources: [
        {
          id: 'NAMS-2022-Position',
          title: 'NAMS 2022 Hormone Therapy Position Statement',
          year: 2022,
          url: 'https://www.menopause.org/professional-resources',
        },
      ],
      clinician_review_required: true,
      red_flags_detected: [],
      learn_signal: 'Accepted 97% of the time for HRT Pathway Step 1',
    },
    {
      id: 'fatima-tlp2-nba-2',
      title: 'Dispatch MenQoL and PSQI PROMs',
      rationale:
        '29-item MenQoL plus Pittsburgh Sleep Quality Index required at HRT Pathway enrollment. Sent via JITAI.',
      confidence: 0.89,
      riskTier: 'review_window',
      actionType: 'dispatch_PROM',
      ctaVerb: 'Dispatch',
      cited_sources: [
        {
          id: 'Scrips-PROM-Engine',
          title: 'Scrips PROM Engine instrument catalog (HRT Pathway)',
          year: 2026,
        },
      ],
      clinician_review_required: true,
      red_flags_detected: [],
      learn_signal: 'Always dispatched at HRT Pathway Step 1',
    },
    {
      id: 'fatima-tlp2-nba-3',
      title: 'Present 5-year symptom trajectory',
      rationale:
        'Show symptom burden before and after HRT. Increases protocol commitment 2.5x.',
      confidence: 0.68,
      riskTier: 'manual_approve',
      // [IMPLEMENTED][PROD-583] Temporary classification: PathwayView is a
      // UX gesture, not yet registered in ADR-001 Layer 2 closed enum.
      // Classified as `send_educational_message` until Phase 2 ADR-001
      // amendment lands (see PROD-583 follow-up ticket).
      actionType: 'send_educational_message',
      ctaVerb: 'Present',
      cited_sources: [
        {
          id: 'CDS-Adoption-Lit-2024',
          title:
            'Pre-pilot CDS adoption literature benchmarks (illustrative projection)',
          year: 2024,
        },
      ],
      clinician_review_required: true,
      red_flags_detected: [],
      learn_signal: 'Shown in 62% of HRT Pathway Step 1 visits',
    },
  ],
  ethnic_subgroup: 'arab_mena',
  halal_eating: true,
  prayer_time_observant: true,
  soapDraft: {
    cc: 'Worsening hot flashes, fragmented sleep, low mood. Discuss HRT.',
    hpi: '51F with 6-month progression of vasomotor symptoms (4 to 6 hot flashes per day, night sweats), early waking and fragmented sleep, and low-grade depressive symptoms (PHQ-9 = 9). No personal history of breast or endometrial cancer. No first-degree relative with BRCA-related malignancy reported on intake. Cycle irregular over 12 months, last period 4 months ago.',
    assessment:
      'Perimenopausal syndrome with significant symptom burden (vasomotor, sleep, mood). Candidate for HRT Pathway HRT optimization. BRCA flag pending family history confirmation; if positive, exclusion at HRT Pathway eligibility per protocol.',
    plan: 'Order HRT Pathway hormone baseline panel. Dispatch MenQoL plus PSQI PROMs via JITAI. Confirm BRCA family history at next visit. Schedule HRT Pathway Step 2 visit at week 2 for results review and HRT initiation decision.',
  },
};
